import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CourseRegisterModal from "../../components/CourseRegisterModal";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

type Course = {
  id: number;
  slug: string;
  title: string;
  description: string;
  additional_description?: string;
  image_url: string | null;
  detail_image_url?: string | null;
  day_time?: string | null;
  age_group?: string | null;
};

const CourseShow: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!slug) return;

    setLoading(true);

    fetch(`/api/courses/${slug}?lang=${i18n.language}`)
      .then((res) => {
        if (!res.ok) throw new Error("course fetch failed");
        return res.json();
      })
      .then((data: Course) => {
        setCourse(data);
        setLoading(false);

        // Opened via the old numeric-id URL: move to the readable slug URL.
        if (/^\d+$/.test(slug) && data.slug && data.slug !== slug) {
          navigate(`/courses/${data.slug}`, { replace: true });
        }
      })
      .catch((err) => {
        console.error("Course fetch error:", err);
        setCourse(null);
        setLoading(false);
      });
  }, [slug, i18n.language]);

  if (loading) {
    return null;
  }

  if (!course) {
    navigate('/course', { replace: true });
    return null;
  }

  const handleSuccessClose = () => {
    setModalOpen(false);
    navigate("/");
  };

  return (
    <>
      <SEO
        title={`${course.title} - ${t("pageTitles.courses")}`}
        description={course.description?.substring(0, 160) ?? ""}
        noindex
      />

      <CourseRegisterModal
        courseId={course.id}
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSuccessClose={handleSuccessClose}
      />

      <div className="max-w-6xl mx-auto px-4 py-12 flex flex-col lg:flex-row gap-10 items-stretch">
        <div className="w-full lg:w-1/2 flex items-center justify-center">
          <img
            src={course.detail_image_url || course.image_url || "/images/courses/default.png"}
            alt={course.title || "course image"}
            className="w-full h-full object-cover rounded-2xl shadow-md"
          />
        </div>

        <div className="w-full lg:w-1/2 flex flex-col justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-6 text-center underline underline-offset-8 decoration-brand-red-text">
              {course.title}
            </h1>
            {(course.day_time || course.age_group) && (
              <p className="text-sm text-gray-400 mb-4 text-center lg:text-left">
                {course.day_time && <span>{t("courses.dayTime")}: {course.day_time}</span>}
                {course.day_time && course.age_group && <span className="mx-2">·</span>}
                {course.age_group && <span>{t("courses.ageGroup")}: {course.age_group}</span>}
              </p>
            )}
            <p className="text-lg text-gray-400 mb-8 leading-relaxed text-center lg:text-left max-w-2xl mx-auto lg:max-w-none lg:mx-0">
              {course.description}
            </p>
          </div>

          <div className="mt-4">
            <button
              onClick={() => setModalOpen(true)}
              className="block w-full text-center px-6 py-3 bg-[#D8001D] text-white font-semibold rounded-xl hover:bg-[#B8001A] transition text-lg cursor-pointer"
            >
              {t("courses.register")}
            </button>
          </div>
        </div>
      </div>

      {course.additional_description && (
        <div className="max-w-6xl mx-auto px-4 mt-8">
          <h2 className="text-3xl font-bold mb-6 text-center underline underline-offset-8 decoration-brand-red-text">
            {t("courses.program_detail")}
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed whitespace-pre-line text-center max-w-2xl mx-auto lg:max-w-none lg:text-left lg:mx-0">
            {course.additional_description}
          </p>
        </div>
      )}
    </>
  );
};

export default CourseShow;